import { Autocomplete } from '@mui/material';
import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui/primitives';
import { TextField } from '@/ui/primitives';
import { filterInputSx } from './filterControlStyles';
import type { ReactElement } from 'react';

type SingleSelectOption = { value: string; label: string };

type SingleSelectFilterFieldProps = {
  value: string;
  options: SingleSelectOption[];
  label: string;
  fontSizePx?: number;
  labelWidthPx?: number;
  labelControlGap?: string;
  inputAriaLabel?: string;
  emptyLabel?: string;
  controlWidthPx?: number;
  inputBackgroundColor?: 'background.paper' | 'background.default';
  inputLeftPadding?: string;
  onChange: (next: string) => void;
};

export const SingleSelectFilterField = ({
  value,
  options,
  label,
  fontSizePx = 15,
  labelWidthPx,
  labelControlGap = s('sm'),
  inputAriaLabel,
  emptyLabel,
  controlWidthPx = 180,
  inputBackgroundColor = 'background.paper',
  inputLeftPadding = s('sm'),
  onChange,
}: SingleSelectFilterFieldProps): ReactElement => {
  const selectedOption = options.find((option) => option.value === value);
  const resolvedControlWidth = `${controlWidthPx}px`;
  const controlHeight = 32;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: labelControlGap,
        minWidth: 0,
        width: 'auto',
      }}
    >
      <Text
        role="label"
        color="textSecondary"
        sx={{ whiteSpace: 'nowrap', fontSize: `${fontSizePx}px`, lineHeight: 1.4 }}
      >
        <Box
          component="span"
          sx={
            labelWidthPx
              ? {
                  display: 'inline-flex',
                  justifyContent: 'flex-end',
                  width: `${labelWidthPx}px`,
                }
              : undefined
          }
        >
          {label}
        </Box>
      </Text>
      <Box
        sx={{
          width: resolvedControlWidth,
          minWidth: resolvedControlWidth,
          maxWidth: resolvedControlWidth,
          flex: `0 0 ${resolvedControlWidth}`,
        }}
      >
        <Autocomplete
          disableClearable
          options={options}
          value={selectedOption}
          isOptionEqualToValue={(option, current) => option.value === current.value}
          getOptionLabel={(option) => option.label}
          onChange={(_event, nextOption) => {
            onChange(nextOption.value);
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: s('md'),
                  py: s('sm'),
                  minHeight: `calc(${s('2xl')} + ${s('md')})`,
                }}
              >
                <Text
                  role="body"
                  component="span"
                  sx={{ fontSize: `${fontSizePx}px`, lineHeight: 1.4 }}
                >
                  {option.label}
                </Text>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              inputHeight={controlHeight}
              placeholder={emptyLabel}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- Autocomplete renderInput params supply inputProps with required refs/handlers.
              inputProps={{
                ...params.inputProps,
                'aria-label': inputAriaLabel ?? label,
                readOnly: true,
                style: { cursor: 'pointer' },
              }}
            />
          )}
          sx={(theme) => ({
            ...(typeof filterInputSx === 'function' ? filterInputSx(theme) : {}),
            width: '100%',
            minWidth: 0,
            '& .MuiAutocomplete-inputRoot': {
              minHeight: `${controlHeight}px !important`,
              height: `${controlHeight}px !important`,
              maxHeight: `${controlHeight}px !important`,
              backgroundColor: inputBackgroundColor,
              alignItems: 'center',
              paddingTop: `${s('none')} !important`,
              paddingBottom: `${s('none')} !important`,
              minWidth: 0,
              cursor: 'pointer',
            },
            '& .MuiAutocomplete-input': {
              minHeight: `${controlHeight}px !important`,
              height: `${controlHeight}px !important`,
              paddingTop: `${s('none')} !important`,
              paddingBottom: `${s('none')} !important`,
              paddingLeft: `${inputLeftPadding} !important`,
              marginLeft: `${s('none')} !important`,
              lineHeight: '1 !important',
              cursor: 'pointer',
            },
            '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
              ...theme.typography.body1,
              fontSize: `${fontSizePx}px`,
              cursor: 'pointer',
            },
            '& .MuiInputBase-input, & .MuiAutocomplete-input, & input': {
              ...theme.typography.body1,
              fontSize: `${fontSizePx}px`,
              cursor: 'pointer',
            },
            '& .MuiInputBase-input::placeholder, & .MuiAutocomplete-input::placeholder, & input::placeholder':
              {
                ...theme.typography.body1,
                fontSize: `${fontSizePx}px`,
                color: theme.palette.text.secondary,
                opacity: 1,
              },
          })}
          slotProps={{
            popper: {
              sx: {
                '& .MuiAutocomplete-paper': {
                  minWidth: resolvedControlWidth,
                  width: 'max-content',
                  maxWidth: 'min(560px, calc(100vw - 32px))',
                },
              },
            },
            paper: {
              sx: {
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                minWidth: resolvedControlWidth,
                width: 'max-content',
                maxWidth: 'min(560px, calc(100vw - 32px))',
              },
            },
            listbox: {
              sx: {
                p: s('none'),
                m: s('none'),
                minWidth: resolvedControlWidth,
                '& .MuiAutocomplete-option': {
                  paddingLeft: `${s('md')} !important`,
                  paddingRight: `${s('md')} !important`,
                  whiteSpace: 'nowrap',
                },
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export type { SingleSelectFilterFieldProps, SingleSelectOption };
